import { CSSProperties, useCallback, useEffect, useState } from 'react';
import { MotionValue, useSpring } from 'motion/react';

import './counter.css';

interface NumberProps {
  mv: MotionValue<number>;
  number: number;
  height: number;
}

function Number({ mv, number, height }: NumberProps) {
  const calculateTransform = useCallback(
    (latest: number) => {
      const placeValue = latest % 10;
      const offset = (10 + number - placeValue) % 10;
      let memo = offset * height;
      if (offset > 5) {
        memo -= 10 * height;
      }

      return memo;
    },
    [number, height],
  );

  // Get initial transform value - handle NaN case
  const initialValue = mv.get();
  const [transformValue, setTransformValue] = useState(() => {
    // If initial value is NaN, default to 0
    const safeInitialValue = isNaN(initialValue) ? 0 : initialValue;
    const yValue = calculateTransform(safeInitialValue);
    return `translateY(${yValue}px)`;
  });

  useEffect(() => {
    const unsubscribe = mv.on('change', (latest) => {
      // Only update if we have a valid number to prevent infinite loops
      if (!isNaN(latest) && isFinite(latest)) {
        const yValue = calculateTransform(latest);
        if (!isNaN(yValue) && isFinite(yValue)) {
          setTransformValue(`translateY(${yValue}px)`);
        }
      }
    });

    return unsubscribe;
  }, [mv, calculateTransform]);

  return (
    <span className="counter-number" style={{ transform: transformValue }}>
      {number}
    </span>
  );
}

interface DigitProps {
  place: number;
  value: number;
  height: number;
  digitStyle?: CSSProperties;
}

function Digit({ place, value, height, digitStyle }: DigitProps) {
  const valueRoundedToPlace = Math.floor(value / place);

  // Ensure we have valid numbers before creating the spring
  const safeValue = isNaN(valueRoundedToPlace) ? 0 : valueRoundedToPlace;

  const animatedValue = useSpring(safeValue, {
    damping: 25, // Much faster than 150
    stiffness: 200, // Much faster than 20
  });

  useEffect(() => {
    const safePlaceValue = isNaN(valueRoundedToPlace) ? 0 : valueRoundedToPlace;
    animatedValue.set(safePlaceValue);
  }, [animatedValue, place, valueRoundedToPlace]);

  return (
    <div className="counter-digit" style={{ height, ...digitStyle }}>
      {Array.from({ length: 10 }, (_, i) => (
        <Number key={i} mv={animatedValue} number={i} height={height} />
      ))}
    </div>
  );
}

export interface CounterProps {
  value: number;
  fontSize?: number;
  padding?: number;
  places?: number[];
  gap?: number;
  borderRadius?: number;
  horizontalPadding?: number;
  textColor?: string;
  fontWeight?: string | number;
  containerStyle?: CSSProperties;
  counterStyle?: CSSProperties;
  digitStyle?: CSSProperties;
  gradientHeight?: number;
  gradientFrom?: string;
  gradientTo?: string;
  topGradientStyle?: CSSProperties;
  bottomGradientStyle?: CSSProperties;
}

export default function Counter({
  value,
  fontSize = 100,
  padding = 0,
  places = [100, 10, 1],
  gap = 8,
  borderRadius = 4,
  horizontalPadding = 8,
  textColor = 'white',
  fontWeight = 'bold',
  containerStyle,
  counterStyle,
  digitStyle,
  gradientHeight = 16,
  gradientFrom = 'black',
  gradientTo = 'transparent',
  topGradientStyle,
  bottomGradientStyle,
}: CounterProps) {
  const height = fontSize + padding;

  const defaultCounterStyle: CSSProperties = {
    fontSize,
    gap: gap,
    borderRadius: borderRadius,
    paddingLeft: horizontalPadding,
    paddingRight: horizontalPadding,
    color: textColor,
    fontWeight: fontWeight,
  };

  const defaultTopGradientStyle: CSSProperties = {
    height: gradientHeight,
    background: `linear-gradient(to bottom, ${gradientFrom}, ${gradientTo})`,
  };

  const defaultBottomGradientStyle: CSSProperties = {
    height: gradientHeight,
    background: `linear-gradient(to top, ${gradientFrom}, ${gradientTo})`,
  };

  return (
    <div className="counter-container" style={containerStyle}>
      <div
        className="counter-counter"
        style={{ ...defaultCounterStyle, ...counterStyle }}
      >
        {places.map((place) => (
          <Digit
            key={place}
            place={place}
            value={value}
            height={height}
            digitStyle={digitStyle}
          />
        ))}
      </div>
      <div className="gradient-container">
        <div
          className="top-gradient"
          style={topGradientStyle ? topGradientStyle : defaultTopGradientStyle}
        />
        <div
          className="bottom-gradient"
          style={
            bottomGradientStyle
              ? bottomGradientStyle
              : defaultBottomGradientStyle
          }
        />
      </div>
    </div>
  );
}
